import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDatabase} from '../context/DatabaseContext';
import {useUserNickname} from '../hooks/useUserNickname';
import {useAuth} from '../providers/AuthProvider';
import {useToast} from '../context/ToastContext';
import {colors} from '../theme/colors';

const MainMenuScreen = () => {
  const navigate = useNavigate();
  const {getEntriesByDate, getAllFoods} = useDatabase();
  const {signOut} = useAuth();
  const {success, error: showError} = useToast();
  const userNickname = useUserNickname();
  const [todayEntries, setTodayEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [foods, setFoods] = useState([]);
  const [filteredFoods, setFilteredFoods] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const handleLogout = async () => {
    try {
      const result = await signOut();
      if (result.success) {
        success('Logged out successfully');
        navigate('/login', { replace: true });
      } else {
        showError(result.error || 'Failed to log out');
      }
    } catch (err) {
      showError('An error occurred while logging out');
    }
  };

  useEffect(() => {
    loadEntriesForDate(selectedDate);
    loadFoods();
  }, [selectedDate]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredFoods([]);
      setShowSearchResults(false);
    } else {
      const filtered = foods.filter(food =>
        food.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredFoods(filtered.slice(0, 5)); // Show max 5 results
      setShowSearchResults(filtered.length > 0);
    }
  }, [searchQuery, foods]);

  const loadEntriesForDate = async (date) => {
    const dateString = date.toISOString().split('T')[0];
    const entries = await getEntriesByDate(dateString);
    setTodayEntries(entries);
  };

  const loadFoods = async () => {
    const foodsList = await getAllFoods();
    setFoods(foodsList || []);
  };

  const handleFoodSelect = (food) => {
    // Navigate to add-food screen with food pre-selected
    navigate(`/add-food?foodId=${food.id}`);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const navigateToDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
  };

  const menuTiles = [
    {
      id: 'daily-values',
      title: 'ערכים יומיים',
      icon: '💪',
      path: '/daily-values',
    },
    {
      id: 'nutrition-menu',
      title: 'תפריט התזונה שלי',
      icon: '🍽️',
      path: '/food-diary',
    },
    {
      id: 'shopping-list',
      title: 'רשימת קניות',
      icon: '📝',
      path: '/shopping-list',
    },
    {
      id: 'water-tracker',
      title: 'מעקב מים',
      icon: '💧',
      path: '/water-tracker',
    },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.userName}>{userNickname} ❤️</div>
          <div style={styles.bellIcon} aria-hidden="true">🔔</div>
        </div>
        <div style={styles.headerCenter}>
          <button
            style={styles.profileIcon}
            onClick={() => navigate('/profile')}
            aria-label="Navigate to profile"
            type="button">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
              <circle cx="20" cy="20" r="18" stroke={colors.primary} strokeWidth="2" fill="none"/>
              <path d="M12 16 Q20 8 28 16" stroke={colors.primary} strokeWidth="2" fill="none"/>
              <circle cx="20" cy="20" r="3" fill={colors.primary}/>
            </svg>
          </button>
        </div>
        <div style={styles.headerRight}>
          <button
            onClick={handleLogout}
            style={styles.logoutButton}
            aria-label="Log out"
            type="button">
            Logout
          </button>
        </div>
      </div>

      {/* Main Menu Tiles */}
      <nav style={styles.tilesGrid} aria-label="Main navigation menu">
        {menuTiles.map(tile => (
          <button
            key={tile.id}
            style={styles.tile}
            onClick={() => navigate(tile.path)}
            className="card-hover"
            aria-label={`Navigate to ${tile.title}`}
            type="button">
            <div style={styles.tileIcon} aria-hidden="true">{tile.icon}</div>
            <div style={styles.tileTitle}>{tile.title}</div>
          </button>
        ))}
      </nav>

      {/* Search Bar */}
      <div style={styles.searchSection}>
        <div style={styles.searchContainer}>
          <input
            type="text"
            placeholder="חיפוש מזון"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (filteredFoods.length > 0) {
                setShowSearchResults(true);
              }
            }}
            onBlur={() => {
              // Delay hiding to allow click on results
              setTimeout(() => setShowSearchResults(false), 200);
            }}
            style={styles.searchInput}
            dir="rtl"
            aria-label="Search for food"
            aria-autocomplete="list"
            aria-expanded={showSearchResults}
            aria-controls="food-search-results"
            id="food-search-input"
          />
          {showSearchResults && filteredFoods.length > 0 && (
            <ul id="food-search-results" style={styles.searchResults} role="listbox" aria-label="Food search results">
              {filteredFoods.map(food => (
                <li
                  key={food.id}
                  style={styles.searchResultItem}
                  onClick={() => handleFoodSelect(food)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleFoodSelect(food);
                    }
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.surfaceAlt;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  role="option"
                  tabIndex={0}
                  aria-label={`Select ${food.name}`}>
                  <div style={styles.searchResultName}>{food.name}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Barcode Scanner Button */}
      <div style={styles.scanSection}>
        <button
          style={styles.scanButton}
          onClick={() => navigate('/scan')}
          className="btn-secondary"
          aria-label="Scan product barcode">
          <span style={styles.scanIcon} aria-hidden="true">📷</span>
          <span style={styles.scanText}>סרוק ברקוד מוצר</span>
        </button>
      </div>

      {/* Meal Log Preview */}
      <div style={styles.mealLogSection}>
        <div style={styles.mealLogTitle}>תיעוד ארוחות</div>
        <div style={styles.dateNavigation}>
          <button
            style={styles.dateNavButton}
            onClick={() => navigateToDate('prev')}
            aria-label="Previous day">
            <span aria-hidden="true">←</span>
          </button>
          <div style={styles.dateText}>
            {formatDate(selectedDate)} - יומן ארוחות
          </div>
          <button
            style={styles.dateNavButton}
            onClick={() => navigateToDate('next')}
            aria-label="Next day">
            <span aria-hidden="true">→</span>
          </button>
        </div>
        <div style={styles.tableHeaders} role="row">
          <div style={styles.tableHeader} role="columnheader">תיאור</div>
          <div style={styles.tableHeader} role="columnheader">כמות</div>
          <div style={styles.tableHeader} role="columnheader">קלוריות</div>
        </div>
        {todayEntries.length > 0 ? (
          <div style={styles.entriesPreview} role="list">
            {todayEntries.slice(0, 3).map(entry => (
              <div key={entry.id} style={styles.entryRow} role="listitem">
                <div style={styles.entryDescription}>{entry.food_name}</div>
                <div style={styles.entryQuantity}>{entry.grams.toFixed(1)}g</div>
                <div style={styles.entryCalories}>{entry.calories.toFixed(1)}</div>
              </div>
            ))}
            {todayEntries.length > 3 && (
              <div style={styles.moreEntries}>
                +{todayEntries.length - 3} עוד...
              </div>
            )}
          </div>
        ) : (
          <div style={styles.noEntries}>אין רשומות לתאריך זה</div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: '100vh',
    paddingBottom: 20,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    paddingTop: '20px',
  },
  headerLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  userName: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: 'bold',
  },
  bellIcon: {
    fontSize: 24,
    color: colors.textPrimary,
  },
  headerCenter: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  profileIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.surface,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `2px solid ${colors.primary}`,
    cursor: 'pointer',
    padding: 0,
    background: 'none',
  },
  headerRight: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  logoutButton: {
    backgroundColor: colors.error,
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    color: colors.textOnPrimary,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  chevronIcon: {
    fontSize: 24,
    color: colors.textPrimary,
    cursor: 'pointer',
  },
  tilesGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    padding: '20px',
    marginBottom: 20,
  },
  tile: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
    transition: 'all 200ms ease',
    minHeight: 120,
    width: '100%',
    fontFamily: 'inherit',
    color: 'inherit',
  },
  tileIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  tileTitle: {
    fontSize: 16,
    color: colors.textPrimary,
    fontWeight: 600,
    textAlign: 'center',
  },
  searchSection: {
    padding: '0 20px',
    marginBottom: 16,
    position: 'relative',
  },
  searchContainer: {
    position: 'relative',
    width: '100%',
  },
  searchInput: {
    width: '100%',
    height: 48,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '12px 16px',
    fontSize: 16,
    color: colors.textPrimary,
    outline: 'none',
    boxSizing: 'border-box',
  },
  searchResults: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    marginTop: 4,
    maxHeight: 200,
    overflowY: 'auto',
    zIndex: 1000,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  searchResultItem: {
    padding: '12px 16px',
    cursor: 'pointer',
    borderBottom: `1px solid ${colors.border}`,
    transition: 'background-color 200ms ease',
  },
  searchResultItemHover: {
    backgroundColor: colors.surfaceAlt,
  },
  searchResultName: {
    fontSize: 16,
    color: colors.textPrimary,
    textAlign: 'right',
  },
  scanSection: {
    padding: '0 20px',
    marginBottom: 24,
  },
  scanButton: {
    width: '100%',
    height: 48,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.textPrimary}`,
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    cursor: 'pointer',
    transition: 'all 200ms ease',
  },
  scanIcon: {
    fontSize: 20,
    color: colors.primary,
  },
  scanText: {
    fontSize: 16,
    color: colors.textPrimary,
    fontWeight: 500,
  },
  mealLogSection: {
    padding: '0 20px',
  },
  mealLogTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: 16,
  },
  dateNavigation: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    gap: 12,
  },
  dateNavButton: {
    background: 'none',
    border: 'none',
    color: colors.primary,
    fontSize: 20,
    cursor: 'pointer',
    padding: 8,
  },
  dateText: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: 500,
  },
  tableHeaders: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: `1px solid ${colors.border}`,
    marginBottom: 8,
  },
  tableHeader: {
    fontSize: 14,
    color: colors.textTertiary,
    fontWeight: 600,
    textDecoration: 'underline',
    flex: 1,
    textAlign: 'center',
  },
  entriesPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  entryRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '8px 0',
  },
  entryDescription: {
    fontSize: 14,
    color: colors.textPrimary,
    flex: 1,
    textAlign: 'right',
  },
  entryQuantity: {
    fontSize: 14,
    color: colors.textTertiary,
    flex: 1,
    textAlign: 'center',
  },
  entryCalories: {
    fontSize: 14,
    color: colors.primary,
    flex: 1,
    textAlign: 'left',
  },
  moreEntries: {
    fontSize: 14,
    color: colors.primary,
    textAlign: 'center',
    padding: '8px 0',
    fontStyle: 'italic',
  },
  noEntries: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
    padding: '20px 0',
  },
};

export default MainMenuScreen;

